import { useEffect, useRef, useState } from "react";
import { mdToHtml } from "../utils/markdown";

/** Put `text` on the clipboard. navigator.clipboard only exists in a secure context (https or localhost),
 *  so opening the app by LAN IP over plain http needs the older execCommand path. */
export async function writeClipboard(text) {
  if (navigator.clipboard && window.isSecureContext) {
    await navigator.clipboard.writeText(text);
    return;
  }
  const ta = document.createElement("textarea");
  ta.value = text;
  ta.setAttribute("readonly", "");
  ta.style.position = "fixed";
  ta.style.opacity = "0";
  document.body.appendChild(ta);
  ta.select();
  const ok = document.execCommand("copy");
  document.body.removeChild(ta);
  if (!ok) throw new Error("copy failed");
}

/** A cell for tab-separated text: tabs and newlines inside a value would break the columns apart. */
const cell = (v) => (v === null || v === undefined ? "" : String(v).replace(/[\t\r\n]+/g, " "));

/** Rows as tab-separated text with a header line — pastes into Excel / Sheets as real columns. */
export function tableText(columns, rows) {
  return [columns.map(cell).join("\t"), ...rows.map((r) => columns.map((c) => cell(r[c])).join("\t"))].join("\n");
}

/** A chart's numbers as a table: one row per label, one column per series. */
export function chartText(spec) {
  const series = spec?.series || [];
  const header = ["", ...series.map((s) => s.name || "value")];
  const rows = (spec?.labels || []).map((l, i) => [l, ...series.map((s) => s.data?.[i])]);
  return [spec?.title, [header, ...rows].map((r) => r.map(cell).join("\t")).join("\n")].filter(Boolean).join("\n");
}

/** KPI cards as "Label: value unit (change)" lines. */
export function kpiText(spec) {
  return (spec?.cards || [])
    .map((c) => {
      const change = typeof c.change === "number" ? ` (${c.change > 0 ? "+" : ""}${c.change}% ${c.change_label || ""})` : "";
      return `${c.label}: ${c.value ?? "—"}${c.unit ? ` ${c.unit}` : ""}${change}`.trim();
    })
    .join("\n");
}

/** Markdown as the plain text a reader sees (no **, no table pipes) — rendered, then read back. */
export function plainText(markdown) {
  const div = document.createElement("div");
  div.innerHTML = mdToHtml(markdown || "");
  div.style.position = "fixed";
  div.style.left = "-9999px";
  document.body.appendChild(div); // innerText needs layout to keep line breaks
  const text = div.innerText ?? div.textContent ?? ""; // innerText keeps line breaks; textContent if absent
  document.body.removeChild(div);
  return text.trim();
}

/** A small "copy" button. `getText` is called on click, so it always copies what is on screen now.
 *  `label` (optional) is shown next to the icon, e.g. "⧉ Copy"; without it the button is just the icon. */
export default function CopyButton({ getText, title = "Copy", label = "", className = "" }) {
  const [state, setState] = useState("idle"); // idle | done | error
  const timer = useRef(null);
  useEffect(() => () => clearTimeout(timer.current), []);

  async function copy(e) {
    e.stopPropagation();
    try {
      await writeClipboard(getText() || "");
      setState("done");
    } catch {
      setState("error");
    }
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setState("idle"), 1500);
  }

  return (
    <button
      type="button"
      className={"copy-btn" + (state !== "idle" ? ` ${state}` : "") + (className ? ` ${className}` : "")}
      title={state === "done" ? "Copied" : state === "error" ? "Couldn't copy" : title}
      aria-label={title}
      onClick={copy}
    >
      {state === "done" ? "✓" : state === "error" ? "✗" : label ? `⧉ ${label}` : "⧉"}
    </button>
  );
}
